import { createTheme } from '@mui/material/styles';

// Professional color palette
const colors = {
  primary: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },
  secondary: {
    50: '#fef7f0',
    100: '#fdeee0',
    200: '#fad5b8',
    300: '#f7b885',
    400: '#f4954f',
    500: '#f17a1a',
    600: '#e26a0a',
    700: '#c55a08',
    800: '#a84a0d',
    900: '#8b3d0e',
  },
  neutral: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
  success: {
    50: '#e8f5e8',
    100: '#c8e6c9',
    200: '#a5d6a7',
    300: '#81c784',
    400: '#66bb6a',
    500: '#4caf50',
    600: '#43a047',
    700: '#388e3c',
    800: '#2e7d32',
    900: '#1b5e20',
  },
  warning: {
    50: '#fff8e1',
    100: '#ffecb3',
    200: '#ffe082',
    300: '#ffd54f',
    400: '#ffca28',
    500: '#ffc107',
    600: '#ffb300',
    700: '#ffa000',
    800: '#ff8f00',
    900: '#ff6f00',
  },
  error: {
    50: '#ffebee',
    100: '#ffcdd2',
    200: '#ef9a9a',
    300: '#e57373',
    400: '#ef5350',
    500: '#f44336',
    600: '#e53935',
    700: '#d32f2f',
    800: '#c62828',
    900: '#b71c1c',
  },
};

export const professionalTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: colors.primary[700], // #334155
      light: colors.primary[500], // #64748b
      dark: colors.primary[900], // #0f172a
      contrastText: '#ffffff',
    },
    secondary: {
      main: colors.secondary[600], // #e26a0a
      light: colors.secondary[400], // #f4954f
      dark: colors.secondary[800], // #a84a0d
      contrastText: '#ffffff',
    },
    background: {
      default: '#fafafa',
      paper: '#ffffff',
    },
    text: {
      primary: '#2c3e50',
      secondary: '#5a6c7d',
    },
    success: {
      main: colors.success[600],
      light: colors.success[400],
      dark: colors.success[800],
    },
    warning: {
      main: colors.warning[600],
      light: colors.warning[400],
      dark: colors.warning[800],
    },
    error: {
      main: colors.error[600],
      light: colors.error[400],
      dark: colors.error[800],
    },
    grey: {
      50: colors.neutral[50],
      100: colors.neutral[100],
      200: colors.neutral[200],
      300: colors.neutral[300],
      400: colors.neutral[400],
      500: colors.neutral[500],
      600: colors.neutral[600],
      700: colors.neutral[700],
      800: colors.neutral[800],
      900: colors.neutral[900],
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 800,
      lineHeight: 1.2,
      letterSpacing: '-0.5px',
      color: '#0f172a',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: '-0.3px',
      color: '#0f172a',
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: '-0.2px',
      color: '#334155',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '-0.1px',
      color: '#334155',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: '#334155',
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: '#334155',
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
      color: '#2c3e50',
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.5,
      color: '#5a6c7d',
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.6,
      color: '#2c3e50',
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.6,
      color: '#5a6c7d',
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: '0.5px',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.4,
      color: '#5a6c7d',
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '1px',
      color: '#5a6c7d',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 2px 4px rgba(51, 65, 85, 0.1)',
    '0 4px 8px rgba(51, 65, 85, 0.12)',
    '0 6px 12px rgba(51, 65, 85, 0.15)',
    '0 8px 16px rgba(51, 65, 85, 0.18)',
    '0 12px 24px rgba(51, 65, 85, 0.2)',
    '0 16px 32px rgba(51, 65, 85, 0.22)',
    '0 20px 40px rgba(51, 65, 85, 0.24)',
    '0 24px 48px rgba(51, 65, 85, 0.26)',
    '0 28px 56px rgba(51, 65, 85, 0.28)',
    '0 32px 64px rgba(51, 65, 85, 0.3)',
    '0 36px 72px rgba(51, 65, 85, 0.32)',
    '0 40px 80px rgba(51, 65, 85, 0.34)',
    '0 44px 88px rgba(51, 65, 85, 0.36)',
    '0 48px 96px rgba(51, 65, 85, 0.38)',
    '0 52px 104px rgba(51, 65, 85, 0.4)',
    '0 56px 112px rgba(51, 65, 85, 0.42)',
    '0 60px 120px rgba(51, 65, 85, 0.44)',
    '0 64px 128px rgba(51, 65, 85, 0.46)',
    '0 68px 136px rgba(51, 65, 85, 0.48)',
    '0 72px 144px rgba(51, 65, 85, 0.5)',
    '0 76px 152px rgba(51, 65, 85, 0.52)',
    '0 80px 160px rgba(51, 65, 85, 0.54)',
    '0 84px 168px rgba(51, 65, 85, 0.56)',
    '0 88px 176px rgba(51, 65, 85, 0.58)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          padding: '10px 24px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(51, 65, 85, 0.15)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #334155 0%, #475569 100%)',
          color: '#ffffff',
          '&:hover': {
            background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
          },
          '&:disabled': {
            background: '#cbd5e1',
            color: '#94a3b8',
          },
        },
        outlined: {
          borderColor: '#64748b',
          color: '#334155',
          '&:hover': {
            backgroundColor: 'rgba(51, 65, 85, 0.04)',
            borderColor: '#334155',
          },
          '&:disabled': {
            borderColor: '#cbd5e1',
            color: '#94a3b8',
          },
        },
        text: {
          color: '#334155',
          '&:hover': {
            backgroundColor: 'rgba(51, 65, 85, 0.04)',
          },
          '&:disabled': {
            color: '#94a3b8',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 20px rgba(51, 65, 85, 0.08)',
          border: '1px solid rgba(51, 65, 85, 0.05)',
          '&:hover': {
            boxShadow: '0 8px 32px rgba(51, 65, 85, 0.12)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(51, 65, 85, 0.1)',
        },
        elevation2: {
          boxShadow: '0 4px 16px rgba(51, 65, 85, 0.12)',
        },
        elevation3: {
          boxShadow: '0 8px 24px rgba(51, 65, 85, 0.15)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#64748b',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#334155',
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 20px rgba(26, 35, 126, 0.3)',
        },
      },
    },
  },
});

export default professionalTheme;
